import React from "react";
import {
  BrowserRouter as Router,
  Route,
  Routes,
  useLocation,
} from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useEffect } from "react";
import { HelmetProvider } from "react-helmet-async";
import { useCurrency } from "context/CurrencyContext";
import { detectLanguage } from "utils/languageUtils";
import Layout from "components/Layout";
import Login from "pages/Login";
import Register from "pages/Register";
import VerifyEmail from "pages/VerifyEmail";
import ForgotPassword from "pages/ForgotPassword";
import ResetPassword from "pages/ResetPassword";
import { AcceptInvitation } from "pages/AcceptInvitation";
import { ToastProvider } from "context/ToastContext";
import NotFound from "pages/NotFound";
import Maintenance from "pages/Maintenance";
import { AuthProvider } from "context/AuthContext";
import { ApiProvider } from "context/ApiContext";
import { CartProvider } from "context/CartContext";
import { CurrencyProvider } from "context/CurrencyContext";
import { CartAnimationProvider } from "context/CartAnimationContext";
import Home from "pages/Home";
import ProductList from "pages/ProductList";
import ProductDetail from "pages/ProductDetail";
import Cart from "pages/Cart";
import OrderConfirmation from "pages/OrderConfirmation";
import CheckoutCancel from "pages/CheckoutCancel";
import PolicyPage from "pages/PolicyPage";
import { GdprDashboard } from "pages/GdprDashboard";
import Dashboard from "pages/admin/Dashboard";
import { StoreGdprDashboard } from "pages/admin/StoreGdprDashboard";
import { SetupWizard } from "pages/admin/SetupWizard";
import Products from "pages/admin/Products";
import AdminOrders from "pages/admin/Orders";
import OrderDetailAdmin from "pages/admin/OrderDetail";
import Users from "pages/admin/Users";
import UserDetail from "pages/admin/UserDetail";
import { Invitations } from "pages/admin/Invitations";
import Categories from "pages/admin/Categories";
import HelpDesk from "pages/admin/HelpDesk";
import ProfitRules from "pages/admin/ProfitRules";
import Shipping from "pages/admin/Shipping";
import Locations from "pages/admin/Locations";
import { BulkLocationManagement } from "pages/admin/BulkLocationManagement";
import Currencies from "pages/admin/Currencies";
import Payments from "pages/admin/Payments";
import { PaymentMethodSettings } from "pages/admin/PaymentMethodSettings";
import { PaymentGatewaySettings } from "pages/admin/PaymentGatewaySettings";
import { ThemeSettings } from "pages/admin/ThemeSettings";
import { LanguageSettings } from "pages/admin/LanguageSettings";
import { AddressFieldSettings } from "pages/admin/AddressFieldSettings";
import Pages from "pages/admin/Pages";
import PageForm from "pages/admin/PageForm";
import { PageBuilderAdmin } from "pages/admin/PageBuilder";
import PageZones from "pages/admin/PageZones";
import Orders from "pages/Orders";
import OrderDetail from "pages/OrderDetail";
import Profile from "pages/Profile";
import Addresses from "pages/Addresses";
import { GoogleReCaptchaProvider } from "@google-recaptcha/react";
import { ChatProvider } from "context/ChatContext";
import { PlatformAuthProvider } from "context/PlatformAuthContext";
import { StoreProvider } from "context/StoreContext";
import PlatformLayout from "components/platform/PlatformLayout";
import PlatformLogin from "pages/platform/Login";
import PlatformDashboard from "pages/platform/Dashboard";
import PlatformStores from "pages/platform/Stores";
import PlatformStoreDetail from "pages/platform/StoreDetail";
import PlatformStoreProducts from "pages/platform/StoreProducts";
import PlatformSubscriptions from "pages/platform/Subscriptions";
import SubscriptionRequests from "pages/platform/SubscriptionRequests";
import PlatformInvoices from "pages/platform/Invoices";
import PlatformAuditLogs from "pages/platform/AuditLogs";
import PlatformBreaches from "pages/platform/Breaches";
import PlatformBreachDetail from "pages/platform/BreachDetail";
import PlatformPlugins from "pages/platform/Plugins";
import PlatformPluginForm from "pages/platform/PluginForm";
import PlatformPlans from "pages/platform/Plans";
import PlatformPlanForm from "pages/platform/PlanForm";
import PlatformPages from "pages/platform/Pages";
import PlatformPageForm from "pages/platform/PageForm";
import PlatformStoreForm from "pages/platform/StoreForm";
import PlatformLanding from "pages/platform/Landing";
import PlatformHome from "pages/platform/PlatformHome";
import Onboarding from "pages/platform/Onboarding";
import HowItWorks from "pages/platform/HowItWorks";
import Pricing from "pages/platform/Pricing";
import Policies from "pages/platform/Policies";
import PolicyDetail from "pages/platform/PolicyDetail";
import Contact from "pages/platform/Contact";
import PlatformPayouts from "pages/platform/Payouts";
import ContactMessages from "pages/platform/ContactMessages";
import StorePayouts from "pages/platform/StorePayouts";
import PlatformIncome from "pages/platform/Income";
import PayoutDetail from "pages/platform/PayoutDetail";
import AdminPayoutDetail from "pages/admin/PayoutDetail";
import ProtectedRoute from "components/platform/ProtectedRoute";
import { Payouts } from "pages/admin/Payouts";
import { ProfitRulesSettings } from "pages/admin/ProfitRulesSettings";
import { ContributorPayouts } from "pages/admin/ContributorPayouts";
import { ProductProfitSettings } from "pages/admin/ProductProfitSettings";
import { CustomizationAssets } from "pages/admin/CustomizationAssets";
import { Gallery } from "pages/admin/Gallery";
import CreateOrder from "pages/admin/CreateOrder";
import { PluginProtectedRoute } from "components/PluginProtectedRoute";
import { isStoreHost, getPlatformDomain } from "utils/platformHost";
import AdminCarts from "pages/admin/Carts";
import CartDetailsAdmin from "pages/admin/CartDetails"
const Checkout = React.lazy(() => import("pages/Checkout"));
const Subscription = React.lazy(() => import("pages/admin/Subscription"));
const GOOGLE_RECAPTCHA_SITE_KEY =
  process.env.REACT_APP_GOOGLE_RECAPTCHA_SITE_KEY;

const RecaptchaGate: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const location = useLocation();
  const shouldEnableRecaptcha =
    location.pathname === "/login" ||
    location.pathname === "/register" ||
    location.pathname === "/forgot-password" ||
    location.pathname === "/reset-password" ||
    location.pathname === "/onboarding" ||
    location.pathname === "/platform/contact";

  if (!shouldEnableRecaptcha || !GOOGLE_RECAPTCHA_SITE_KEY) {
    return <>{children}</>;
  }

  return (
    <GoogleReCaptchaProvider
      type="v3"
      siteKey={GOOGLE_RECAPTCHA_SITE_KEY as string}
    >
      {children}
    </GoogleReCaptchaProvider>
  );
};

const AppContent: React.FC = () => {
  const location = useLocation();
  const isPlatformRoute = location.pathname.startsWith("/platform");
  const onSubdomain = isStoreHost();

  // Block platform routes on store hosts (subdomains or custom domains) - redirect to platform domain
  if (isPlatformRoute && onSubdomain) {
    const platformDomain = getPlatformDomain();
    const protocol =
      typeof window !== "undefined" ? window.location.protocol : "https:";

    // Construct platform URL
    const platformUrl = `${protocol}//${platformDomain}`;

    if (typeof window !== "undefined") {
      window.location.href = `${platformUrl}/platform${location.pathname.replace("/platform", "") || "/"}`;
      return null;
    }

    // Fallback: show error message
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center p-8">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">
            Platform Portal Unavailable
          </h1>
          <p className="text-gray-600 mb-4">
            The platform admin portal can only be accessed from the main domain.
          </p>
          <p className="text-sm text-gray-500">
            Please access it from the platform domain instead of a store domain.
          </p>
        </div>
      </div>
    );
  }

  return (
    <>
      <React.Suspense fallback={<div className="min-h-screen" />}>
        <Routes>
          {/* Platform Admin Routes (separate from store routes) - only on main domain */}
          {!onSubdomain && (
            <Route
              path="/platform/*"
              element={
                <PlatformAuthProvider>
                  <Routes>
                    <Route path="/" element={<PlatformLanding />} />
                    <Route path="/login" element={<PlatformLogin />} />
                    <Route
                      path="/*"
                      element={
                        <ProtectedRoute>
                          <PlatformLayout>
                            <Routes>
                              <Route
                                path="/dashboard"
                                element={<PlatformDashboard />}
                              />
                              <Route
                                path="/stores"
                                element={<PlatformStores />}
                              />
                              <Route
                                path="/stores/new"
                                element={<PlatformStoreForm />}
                              />
                              <Route
                                path="/stores/:storeId/edit"
                                element={<PlatformStoreForm />}
                              />
                              <Route
                                path="/stores/:storeId"
                                element={<PlatformStoreDetail />}
                              />
                              <Route
                                path="/stores/:storeId/products"
                                element={<PlatformStoreProducts />}
                              />
                              <Route
                                path="/subscriptions"
                                element={<PlatformSubscriptions />}
                              />
                              <Route
                                path="/subscription-requests"
                                element={<SubscriptionRequests />}
                              />
                              <Route
                                path="/invoices"
                                element={<PlatformInvoices />}
                              />
                              <Route
                                path="/audit-logs"
                                element={<PlatformAuditLogs />}
                              />
                              <Route
                                path="/breaches"
                                element={<PlatformBreaches />}
                              />
                              <Route
                                path="/breaches/:breachId"
                                element={<PlatformBreachDetail />}
                              />
                              <Route
                                path="/plugins"
                                element={<PlatformPlugins />}
                              />
                              <Route
                                path="/plugins/new"
                                element={<PlatformPluginForm />}
                              />
                              <Route
                                path="/plugins/:pluginId"
                                element={<PlatformPluginForm />}
                              />
                              <Route
                                path="/plans"
                                element={<PlatformPlans />}
                              />
                              <Route
                                path="/plans/new"
                                element={<PlatformPlanForm />}
                              />
                              <Route
                                path="/plans/:planId"
                                element={<PlatformPlanForm />}
                              />
                              <Route
                                path="/pages"
                                element={<PlatformPages />}
                              />
                              <Route
                                path="/pages/new"
                                element={<PlatformPageForm />}
                              />
                              <Route
                                path="/pages/:slug"
                                element={<PlatformPageForm />}
                              />
                              <Route
                                path="/payouts"
                                element={<PlatformPayouts />}
                              />
                              <Route
                                path="/payouts/:payoutId"
                                element={<PayoutDetail />}
                              />
                              <Route
                                path="/contact-messages"
                                element={<ContactMessages />}
                              />
                              <Route
                                path="/payouts/stores/:storeId"
                                element={<StorePayouts />}
                              />
                              <Route
                                path="/income"
                                element={<PlatformIncome />}
                              />
                              <Route
                                path="*"
                                element={
                                  <div className="p-8">Page not found</div>
                                }
                              />
                            </Routes>
                          </PlatformLayout>
                        </ProtectedRoute>
                      }
                    />
                  </Routes>
                </PlatformAuthProvider>
              }
            />
          )}

          {/* Maintenance Route - Standalone, no store/theme dependencies */}
          <Route path="/maintenance" element={<Maintenance />} />

          {/* Public Platform Routes (only on main domain, not subdomains) */}
          {!onSubdomain && (
            <Route
              path="/*"
              element={
                <PlatformAuthProvider>
                  <Routes>
                    <Route path="/" element={<PlatformHome />} />
                    <Route path="/onboarding" element={<Onboarding />} />
                    <Route path="/how-it-works" element={<HowItWorks />} />
                    <Route path="/pricing" element={<Pricing />} />
                    <Route path="/policies" element={<Policies />} />
                    <Route path="/policies/:slug" element={<PolicyDetail />} />
                    <Route path="/contact" element={<Contact />} />
                    <Route path="*" element={<NotFound />} />
                  </Routes>
                </PlatformAuthProvider>
              }
            />
          )}

          {/* Store Routes (existing customer and store admin routes) - only on subdomains */}
          {onSubdomain && (
            <>
              {/* Setup Wizard - Standalone, no Layout wrapper */}
              <Route
                path="/admin/setup-wizard"
                element={
                  <StoreProvider>
                    <SetupWizard />
                  </StoreProvider>
                }
              />
              <Route
                path="/*"
                element={
                  <StoreProvider>
                    <Layout>
                      <Routes>
                        <Route path="/" element={<Home />} />
                        <Route path="/login" element={<Login />} />
                        <Route path="/register" element={<Register />} />
                        <Route path="/verify-email" element={<VerifyEmail />} />
                        <Route
                          path="/accept-invitation"
                          element={<AcceptInvitation />}
                        />
                        <Route
                          path="/forgot-password"
                          element={<ForgotPassword />}
                        />
                        <Route
                          path="/reset-password"
                          element={<ResetPassword />}
                        />
                        <Route path="/products" element={<ProductList />} />
                        <Route
                          path="/products/:handle"
                          element={<ProductDetail />}
                        />
                        <Route path="/cart" element={<Cart />} />
                        <Route path="/checkout" element={<Checkout />} />
                        <Route
                          path="/checkout/success"
                          element={<OrderConfirmation />}
                        />
                        <Route
                          path="/checkout/cancel"
                          element={<CheckoutCancel />}
                        />
                        <Route path="/profile" element={<Profile />} />
                        <Route path="/addresses" element={<Addresses />} />
                        <Route path="/gdpr" element={<GdprDashboard />} />
                        <Route path="/orders" element={<Orders />} />
                        <Route
                          path="/orders/:orderId"
                          element={<OrderDetail />}
                        />
                        <Route path="/pages/:slug" element={<PolicyPage />} />
                        <Route
                          path="/admin/dashboard"
                          element={<Dashboard />}
                        />
                        <Route path="/admin/products" element={<Products />} />
                        <Route
                          path="/admin/products/:productId/profit-settings"
                          element={
                            <PluginProtectedRoute pluginId="profit-rules">
                              <ProductProfitSettings />
                            </PluginProtectedRoute>
                          }
                        />
                        <Route path="/admin/orders" element={<AdminOrders />} />
                        <Route path="/admin/carts" element={<AdminCarts />} />
                        <Route
                          path="/admin/orders/create"
                          element={<CreateOrder />}
                        />
                        <Route
                          path="/admin/orders/:orderId"
                          element={<OrderDetailAdmin />}
                        />
                        <Route 
                        path="/admin/carts/:cartId"
                        element={<CartDetailsAdmin />}
                        />
                        <Route path="/admin/users" element={<Users />} />
                        <Route
                          path="/admin/users/:userId"
                          element={<UserDetail />}
                        />
                        <Route
                          path="/admin/invitations"
                          element={<Invitations />}
                        />
                        <Route
                          path="/admin/categories"
                          element={<Categories />}
                        />
                        <Route path="/admin/shipping" element={<Shipping />} />
                        <Route
                          path="/admin/locations"
                          element={<Locations />}
                        />
                        <Route
                          path="/admin/inventory"
                          element={<BulkLocationManagement />}
                        />
                        <Route
                          path="/admin/currencies"
                          element={<Currencies />}
                        />
                        <Route path="/admin/payments" element={<Payments />} />
                        <Route
                          path="/admin/payment-methods"
                          element={<PaymentMethodSettings />}
                        />
                        <Route
                          path="/admin/payment-gateways"
                          element={<PaymentGatewaySettings />}
                        />
                        <Route
                          path="/admin/theme-settings"
                          element={<ThemeSettings />}
                        />
                        <Route
                          path="/admin/language-settings"
                          element={<LanguageSettings />}
                        />
                        <Route
                          path="/admin/address-field-settings"
                          element={<AddressFieldSettings />}
                        />
                        <Route
                          path="/admin/payouts"
                          element={
                            <PluginProtectedRoute pluginId="profit-rules">
                              <Payouts />
                            </PluginProtectedRoute>
                          }
                        />
                        <Route
                          path="/admin/payouts/:payoutId"
                          element={
                            <PluginProtectedRoute pluginId="profit-rules">
                              <AdminPayoutDetail />
                            </PluginProtectedRoute>
                          }
                        />
                        <Route
                          path="/admin/subscription"
                          element={<Subscription />}
                        />
                        <Route
                          path="/admin/store-gdpr"
                          element={<StoreGdprDashboard />}
                        />
                        <Route path="/admin/pages" element={<Pages />} />
                        <Route path="/admin/pages/new" element={<PageForm />} />
                        <Route
                          path="/admin/pages/:pageId"
                          element={<PageForm />}
                        />
                        <Route
                          path="/admin/page-builder"
                          element={<PageBuilderAdmin />}
                        />
                        <Route
                          path="/admin/page-zones"
                          element={<PageZones />}
                        />
                        <Route path="/admin/helpdesk" element={<HelpDesk />} />
                        <Route
                          path="/admin/profit-rules"
                          element={
                            <PluginProtectedRoute pluginId="profit-rules">
                              <ProfitRules />
                            </PluginProtectedRoute>
                          }
                        />
                        <Route
                          path="/admin/profit-rules-settings"
                          element={
                            <PluginProtectedRoute pluginId="profit-rules">
                              <ProfitRulesSettings />
                            </PluginProtectedRoute>
                          }
                        />
                        <Route
                          path="/admin/contributor-payouts"
                          element={
                            <PluginProtectedRoute pluginId="profit-rules">
                              <ContributorPayouts />
                            </PluginProtectedRoute>
                          }
                        />
                        <Route path="/admin/gallery" element={<Gallery />} />
                        <Route
                          path="/admin/customization-assets"
                          element={
                            <PluginProtectedRoute pluginId="product-customization">
                              <CustomizationAssets />
                            </PluginProtectedRoute>
                          }
                        />
                        <Route path="*" element={<NotFound />} />
                      </Routes>
                    </Layout>
                  </StoreProvider>
                }
              />
            </>
          )}

          {/* Catch-all for platform routes when not on subdomain */}
          {!onSubdomain && <Route path="*" element={<NotFound />} />}
        </Routes>
      </React.Suspense>
    </>
  );
};

// Component to handle language detection based on country
// Must be inside CurrencyProvider
const LanguageDetector: React.FC = () => {
  const { i18n } = useTranslation();
  const { location: userLocation } = useCurrency();

  // Update language when country is detected (if no saved preference)
  useEffect(() => {
    const savedLanguage = localStorage.getItem("language");

    // Only auto-detect if no saved preference
    if (!savedLanguage && userLocation?.countryCode) {
      // Skip localStorage check since we already verified there's no saved preference
      const detectedLang = detectLanguage(userLocation.countryCode, true);

      if (detectedLang !== i18n.language) {
        i18n.changeLanguage(detectedLang);
      }
    }
  }, [userLocation?.countryCode, i18n]);

  return null;
};

const App: React.FC = () => {
  const { i18n } = useTranslation();

  useEffect(() => {
    document.documentElement.dir = i18n.language === "ar" ? "rtl" : "ltr";
  }, [i18n.language]);

  const appContent = (
    <AuthProvider>
      <ApiProvider>
        <CurrencyProvider>
          <LanguageDetector />
          <CartProvider>
            <CartAnimationProvider>
              <ChatProvider>
                <ToastProvider>
                  <AppContent />
                </ToastProvider>
              </ChatProvider>
            </CartAnimationProvider>
          </CartProvider>
        </CurrencyProvider>
      </ApiProvider>
    </AuthProvider>
  );

  return (
    <HelmetProvider>
      <Router>
        <RecaptchaGate>{appContent}</RecaptchaGate>
      </Router>
    </HelmetProvider>
  );
};

export default App;
